import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
  type FetchResult,
  type Operation,
} from "@apollo/client/core";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { onError } from "@apollo/client/link/error";
import {
  clearAuthSession,
  ensureFreshAccessToken,
  getCurrentAuthUser,
  refreshAccessToken,
} from "@app/lib/auth/auth-session";
import { emitSessionExpired } from "@app/lib/auth/auth-events";

const graphqlUrl =
  import.meta.env.VITE_EVENTS_GRAPHQL_URL ?? "http://localhost:4001/graphql";
const eventsApiKey = import.meta.env.VITE_EVENTS_API_KEY;
const GRAPHQL_REQUEST_TIMEOUT_MS = 30_000;

export class GraphQLRequestTimeoutError extends Error {
  readonly code = "GRAPHQL_REQUEST_TIMEOUT";

  constructor(timeoutMs: number, cause?: unknown) {
    super(`GraphQL request timed out after ${timeoutMs}ms.`, { cause });
    this.name = "GraphQLRequestTimeoutError";
  }
}

type AuthLinkContext = {
  skipAuth?: boolean;
};

const GUEST_USER_ID = "anonymous:guest";

function shouldSkipAuth(operation: Operation) {
  return (operation.getContext() as AuthLinkContext).skipAuth === true;
}

const apiKeyLink = new ApolloLink((operation, forward) => {
  if (!forward) {
    throw new Error("Apollo API key link requires a next link in the chain.");
  }

  operation.setContext((currentContext: { headers?: HeadersInit }) => ({
    ...currentContext,
    headers: {
      ...currentContext.headers,
      ...(eventsApiKey ? { "x-api-key": eventsApiKey } : {}),
    },
  }));

  return forward(operation);
});

function getNumericStatusCode(candidate: unknown): number | null {
  if (typeof candidate !== "object" || candidate === null) {
    return null;
  }

  const maybeStatus = (candidate as { statusCode?: unknown; status?: unknown })
    .statusCode;
  if (typeof maybeStatus === "number") {
    return maybeStatus;
  }

  const fallbackStatus = (candidate as { status?: unknown }).status;
  return typeof fallbackStatus === "number" ? fallbackStatus : null;
}

function hasUnauthorizedGraphQLError(errors: unknown): boolean {
  if (!Array.isArray(errors)) {
    return false;
  }

  return errors.some((entry) => {
    if (typeof entry !== "object" || entry === null) {
      return false;
    }

    const graphQLError = entry as {
      message?: unknown;
      extensions?: { code?: unknown };
    };
    const extensionCode = graphQLError.extensions?.code;
    const message = graphQLError.message;

    return (
      extensionCode === "UNAUTHENTICATED" ||
      (typeof message === "string" && message.toLowerCase().includes("401"))
    );
  });
}

function isUnauthorizedError(error: unknown): boolean {
  if (getNumericStatusCode(error) === 401) {
    return true;
  }

  if (typeof error !== "object" || error === null) {
    return false;
  }

  const networkError = (error as { networkError?: unknown }).networkError;

  if (getNumericStatusCode(networkError) === 401) {
    return true;
  }

  const graphQLErrors = (error as { graphQLErrors?: unknown }).graphQLErrors;
  if (hasUnauthorizedGraphQLError(graphQLErrors)) {
    return true;
  }

  const combinedErrors = (error as { errors?: unknown }).errors;
  return hasUnauthorizedGraphQLError(combinedErrors);
}

export const withAuthHeaderLink = new ApolloLink((operation, forward) => {
  if (!forward) {
    throw new Error("Apollo auth link requires a next link in the chain.");
  }

  if (shouldSkipAuth(operation)) {
    operation.setContext((currentContext: { headers?: HeadersInit }) => ({
      ...currentContext,
      headers: {
        ...currentContext.headers,
        "x-user-id": GUEST_USER_ID,
      },
    }));
    return forward(operation);
  }

  return new Observable<FetchResult>((observer) => {
    let subscription:
      | {
          unsubscribe: () => void;
        }
      | undefined;

    const execute = async () => {
      let accessToken: string | null;

      try {
        accessToken = await ensureFreshAccessToken();
      } catch {
        clearAuthSession();
        emitSessionExpired();
        observer.error(new Error("Session expired. Please log in again."));
        return;
      }

      if (!accessToken) {
        emitSessionExpired();
        observer.error(new Error("Session expired. Please log in again."));
        return;
      }

      const authUser = getCurrentAuthUser();

      operation.setContext((currentContext: { headers?: HeadersInit }) => ({
        ...currentContext,
        headers: {
          ...currentContext.headers,
          Authorization: `Bearer ${accessToken}`,
          ...(authUser?.id ? { "x-user-id": authUser.id } : {}),
        },
      }));

      subscription = forward(operation).subscribe({
        next: (result) => observer.next(result),
        error: (error) => observer.error(error),
        complete: () => observer.complete(),
      });
    };

    void execute().catch((error) => observer.error(error));

    return () => {
      subscription?.unsubscribe();
    };
  });
});

export const retryUnauthorizedLink = new ApolloLink((operation, forward) => {
  if (!forward) {
    throw new Error(
      "Apollo unauthorized retry link requires a next link in the chain.",
    );
  }

  return new Observable<FetchResult>((observer) => {
    let subscription:
      | {
          unsubscribe: () => void;
        }
      | undefined;

    const runOperation = () => {
      subscription = forward(operation).subscribe({
        next: (result) => observer.next(result),
        complete: () => observer.complete(),
        error: (operationError) => {
          if (shouldSkipAuth(operation)) {
            observer.error(operationError);
            return;
          }

          const hasRetried = operation.getContext().hasRetriedAuth === true;

          if (!hasRetried && isUnauthorizedError(operationError)) {
            operation.setContext({
              hasRetriedAuth: true,
            });

            void refreshAccessToken()
              .then((refreshedAccessToken) => {
                const authUser = getCurrentAuthUser();

                operation.setContext(
                  (currentContext: { headers?: HeadersInit }) => ({
                    ...currentContext,
                    headers: {
                      ...currentContext.headers,
                      Authorization: `Bearer ${refreshedAccessToken}`,
                      ...(authUser?.id ? { "x-user-id": authUser.id } : {}),
                    },
                  }),
                );

                runOperation();
              })
              .catch((refreshError) => {
                emitSessionExpired();
                observer.error(refreshError);
              });
            return;
          }

          observer.error(operationError);
        },
      });
    };

    runOperation();

    return () => {
      subscription?.unsubscribe();
    };
  });
});

const errorLink = onError(({ error, operation }) => {
  const operationName = operation.operationName || "anonymous";

  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((graphQLError) => {
      console.error(
        `[Apollo GraphQL error] ${operationName}: ${graphQLError.message}`,
      );
    });
    return;
  }

  console.error(`[Apollo network error] ${operationName}:`, error.message);
});

const fetchWithDeadline: typeof fetch = async (input, init) => {
  const timeoutSignal = AbortSignal.timeout(GRAPHQL_REQUEST_TIMEOUT_MS);
  const callerSignal = init?.signal;

  try {
    return await fetch(input, {
      ...init,
      signal: callerSignal
        ? AbortSignal.any([callerSignal, timeoutSignal])
        : timeoutSignal,
    });
  } catch (error) {
    if (timeoutSignal.aborted && !callerSignal?.aborted) {
      throw new GraphQLRequestTimeoutError(GRAPHQL_REQUEST_TIMEOUT_MS, error);
    }
    throw error;
  }
};

const httpLink = new HttpLink({
  uri: graphqlUrl,
  fetch: fetchWithDeadline,
});

const cache = new InMemoryCache({
  typePolicies: {
    Event: {
      keyFields: ["eventId"],
      fields: {
        // `listEvents` does not reliably return `destination` (it comes back
        // empty), while `getEvent` does. Since both responses normalize into
        // the same Event entity, a list refetch would otherwise wipe the
        // destination written by the detail query, causing it to flash and
        // disappear in the table. Keep the existing non-empty value when the
        // incoming one is empty/null.
        destination: {
          merge(existing: string | undefined, incoming: string | undefined) {
            return incoming != null && incoming !== "" ? incoming : existing;
          },
        },
      },
    },
    Attendee: { keyFields: ["attendeeId"] },
    Session: { keyFields: ["sessionId"] },
    Calendar: { keyFields: ["calendarId"] },
    PersonalSchedule: { keyFields: ["scheduleId"] },
    EventPolicy: { keyFields: ["policyId"] },
    BookingManifestEntry: { keyFields: ["bookingManifestId"] },
  },
});

export const eventsAdminApolloClient = new ApolloClient({
  link: ApolloLink.from([
    apiKeyLink,
    withAuthHeaderLink,
    retryUnauthorizedLink,
    errorLink,
    httpLink,
  ]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
    query: {
      fetchPolicy: "cache-first",
    },
  },
});
